import React from "react";
import { GoogleMap, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { MapPin, ExternalLink } from "lucide-react";

const MAPS_LIBRARIES = ["places", "drawing"];

export default function DriverLocationDialog({ open, onOpenChange, driver, unitLabel = "Unidad", Icon = MapPin }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: MAPS_LIBRARIES,
  });

  const lat = typeof driver?.current_lat === "number" ? driver.current_lat : null;
  const lng = typeof driver?.current_lng === "number" ? driver.current_lng : null;
  const hasLocation = lat != null && lng != null;

  const lastSeen = driver?.last_seen_at
    ? new Date(driver.last_seen_at).toLocaleString("es")
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="driver-map-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
            <span className="inline-flex h-9 w-9 rounded-full bg-company/10 text-[color:var(--company-primary)] border border-company/20 items-center justify-center shrink-0">
              <Icon size={16} />
            </span>
            <span className="min-w-0">
              <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 block -mb-0.5">{unitLabel}</span>
              <span className="block truncate">{driver?.name || "Sin nombre"}</span>
            </span>
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-0.5">
            {hasLocation ? (
              <span className="font-mono text-xs">{lat.toFixed(5)}, {lng.toFixed(5)}</span>
            ) : (
              <span>Sin coordenadas registradas todavía.</span>
            )}
            {lastSeen && (
              <span className="text-xs text-neutral-500">Última señal: {lastSeen}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50" style={{ height: 420 }}>
          {!hasLocation ? (
            <div className="h-full w-full flex flex-col items-center justify-center text-neutral-400 gap-2">
              <MapPin size={28} />
              <p className="text-sm">Esta unidad todavía no ha reportado posición a Samsara.</p>
            </div>
          ) : loadError ? (
            <div className="h-full w-full flex items-center justify-center text-sm text-red-600 p-4 text-center">
              No se pudo cargar Google Maps. Revisa la API key.
            </div>
          ) : !isLoaded ? (
            <div className="h-full w-full flex items-center justify-center text-sm text-neutral-400">
              Cargando mapa…
            </div>
          ) : (
            <GoogleMap
              center={{ lat, lng }}
              zoom={15}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              options={{ streetViewControl: false, fullscreenControl: false, mapTypeControl: false }}
            >
              <OverlayView
                position={{ lat, lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h })}
              >
                <div className="flex flex-col items-center pointer-events-none" title={driver?.name}>
                  <div className="h-10 w-10 rounded-full bg-company text-[color:var(--company-primary-contrast)] shadow-lg ring-4 ring-white flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <div
                    className="w-0 h-0 -mt-1"
                    style={{
                      borderLeft: "6px solid transparent",
                      borderRight: "6px solid transparent",
                      borderTop: "8px solid var(--company-primary)",
                    }}
                  />
                </div>
              </OverlayView>
            </GoogleMap>
          )}
        </div>

        {hasLocation && (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--company-primary)] hover:underline justify-end"
            data-testid="driver-open-in-gmaps"
          >
            Abrir en Google Maps <ExternalLink size={13} />
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}
